import React, { useState, useEffect } from "react";
import Topbar from "./Topbar";
import Sidebar from "./Sidebar";
import styles from "../assets/css/Dashboard.module.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaFilter, FaCalendarAlt } from "react-icons/fa";
import DataTable from "react-data-table-component";
import DepartmentSidebar from "../pages/SuperAdmin/components/DepertmentSidebar";
import DepartmentTopbar from "../pages/SuperAdmin/components/DepartmentTopbar";

// Helper function to convert DD-MM-YYYY to YYYY-MM-DD
const convertToYYYYMMDD = (dateStr) => {
  if (!dateStr) return "";
  const [day, month, year] = dateStr.split("-");
  return `${year}-${month}-${day}`;
};

// Helper function to convert YYYY-MM-DD to DD-MM-YYYY
const convertToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`;
};

// Helper function to format date from YYYY-MM-DD HH:mm:ss to DD-MM-YYYY
const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  // Extract just the date part if it includes time
  const datePart = dateStr.split(" ")[0];
  const [year, month, day] = datePart.split("-");
  return `${day}-${month}-${year}`;
};

function DetailsPage({
  title,
  backPath,
  columns,
  data,
  onRowClick,
  onDateFilterChange,
  showTotalAmount = true,
  isLoading = false,
  totalAmount = "0.00",
  dateField,
}) {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredData, setFilteredData] = useState(data);
  const [departmentType, setDepartmentType] = useState("");

  useEffect(() => {
    setDepartmentType(localStorage.getItem("type"));
  }, []);
  console.log("data", data);
  console.log("filteredData", filteredData);

  // Transform columns for react-data-table-component
  const tableColumns = columns.map((column) => ({
    name: column.header,
    selector: (row) => row[column.field],
    sortable: true,
    cell: (row) => {
      const value = row[column.field];
      if (
        value &&
        typeof value === "string" &&
        value.match(/^\d{4}-\d{2}-\d{2}/)
      ) {
        return formatDateToDDMMYYYY(value);
      }
      return column.render ? column.render(value, row) : value;
    },
  }));

  // Update filtered data when dates change
  const handleDateChange = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    // Convert start and end dates to YYYY-MM-DD if needed
    let startDateYYYYMMDD = startDate;
    let endDateYYYYMMDD = endDate;
    if (/^\d{2}-\d{2}-\d{4}$/.test(startDate)) {
      const [day, month, year] = startDate.split("-");
      startDateYYYYMMDD = `${year}-${month}-${day}`;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(endDate)) {
      const [day, month, year] = endDate.split("-");
      endDateYYYYMMDD = `${year}-${month}-${day}`;
    }

    if (new Date(startDateYYYYMMDD) > new Date(endDateYYYYMMDD)) {
      alert("Start date cannot be later than end date");
      return;
    }

    const filtered = data.filter((row) => {
      const rowDate = row[dateField];
      if (!rowDate) {
        console.log("No date found for field:", dateField, "in row:", row);
        return false;
      }
      // Convert row date to YYYY-MM-DD if needed
      let rowDateStr = rowDate.split(" ")[0];
      if (/^\d{2}-\d{2}-\d{4}$/.test(rowDateStr)) {
        const [day, month, year] = rowDateStr.split("-");
        rowDateStr = `${year}-${month}-${day}`;
      }
      const rowDateObj = new Date(rowDateStr);
      const startDateObj = new Date(startDateYYYYMMDD);
      const endDateObj = new Date(endDateYYYYMMDD);

      // Set time to start of day for start date and end of day for end date
      startDateObj.setHours(0, 0, 0, 0);
      endDateObj.setHours(23, 59, 59, 999);

      const isInRange = rowDateObj >= startDateObj && rowDateObj <= endDateObj;
      return isInRange;
    });

    setFilteredData(filtered);
    if (onDateFilterChange) {
      onDateFilterChange(startDate, endDate, filtered);
    }
  };

  // Initialize filtered data when data changes
  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Reset dates handler
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setFilteredData(data);
    if (onDateFilterChange) {
      onDateFilterChange("", "", data);
    }
  };

  // Custom styles for the data table
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "1px solid #dee2e6",
        fontSize: "0.875rem",
        fontWeight: "500",
        color: "#495057",
      },
    },
    rows: {
      style: {
        fontSize: "0.875rem",
        color: "#212529",
        "&:hover": {
          backgroundColor: "#f8f9fa",
          cursor: onRowClick ? "pointer" : "default",
        },
      },
    },
    cells: {
      style: {
        padding: "12px 16px",
      },
    },
  };

  return (
    <>
      {departmentType === "Sanitary" ? <DepartmentTopbar /> : <Topbar />}
      <div className="d-flex">
        {departmentType === "Sanitary" ? <DepartmentSidebar /> : <Sidebar />}
        <div className={styles.dashboardContainer}>
          <div className={styles.dashboardContent}>
            {/* Header Section */}
            <div className="d-flex align-items-center mb-4">
              <button
                className="btn btn-link text-primary p-0 me-3"
                onClick={() => navigate(backPath)}
                style={{ textDecoration: "none" }}
              >
                <FaArrowLeft className="me-1" />
                Back
              </button>
              <h2
                className="mb-0"
                style={{
                  color: "#2c3e50",
                  fontWeight: "500",
                  fontSize: "1.5rem",
                }}
              >
                {title}
              </h2>
            </div>

            {/* Filter and Total Amount Section */}
            <div
              className="card mb-4"
              style={{
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <div className="card-body p-3">
                <div className="row g-3 align-items-end">
                  <div className="col-auto">
                    <div className="d-flex align-items-center mb-2">
                      <FaFilter
                        className="text-primary me-2"
                        style={{ fontSize: "0.875rem" }}
                      />
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.875rem" }}
                      >
                        Filter
                      </span>
                    </div>
                  </div>
                  <div className="col-auto">
                    <label className="form-label small text-muted mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={startDate ? convertToYYYYMMDD(startDate) : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setStartDate(value ? convertToDDMMYYYY(value) : "");
                      }}
                      style={{
                        width: "160px",
                        borderColor: "#dee2e6",
                      }}
                    />
                  </div>
                  <div className="col-auto">
                    <label className="form-label small text-muted mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={endDate ? convertToYYYYMMDD(endDate) : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEndDate(value ? convertToDDMMYYYY(value) : "");
                      }}
                      style={{
                        width: "160px",
                        borderColor: "#dee2e6",
                      }}
                    />
                  </div>
                  <div className="col-auto">
                    <button
                      className="btn btn-primary btn-sm px-4"
                      onClick={handleDateChange}
                      disabled={!startDate || !endDate}
                    >
                      Apply Filter
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm px-3 ms-2"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                  </div>
                  {showTotalAmount && (
                    <div className="col-auto ms-auto">
                      <div
                        className="bg-primary text-white px-4 py-2"
                        style={{
                          borderRadius: "6px",
                        }}
                      >
                        <div className="small mb-0">Total Amount</div>
                        <div className="h5 mb-0">₹{totalAmount}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div
              className="card"
              style={{
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <div className="card-body p-0">
                <DataTable
                  columns={tableColumns}
                  data={filteredData}
                  customStyles={customStyles}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[10, 20, 30, 50]}
                  highlightOnHover
                  pointerOnHover={!!onRowClick}
                  onRowClicked={onRowClick}
                  progressPending={isLoading}
                  progressComponent={
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  }
                  noDataComponent={
                    <div className="text-center py-4 text-muted">
                      No data found for the selected date range
                    </div>
                  }
                />
              </div>
            </div>

            {/* Report Date Range */}
            <div
              className="mt-3 d-flex align-items-center text-muted"
              style={{
                fontSize: "0.875rem",
              }}
            >
              <FaCalendarAlt className="me-2" />
              Report generated for the period: {startDate || "-"} to{" "}
              {endDate || "-"}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DetailsPage;
